<template>
  <div class="paginator">
    <button
      class="paginator__button paginator__button--control"
      :class="{ 'paginator__button--disabled': current <= 1 }"
      :aria-label="i18n.paginatorPrevious"
      @click="previous(), gotoScroll()"
    >
      <svg
        width="13"
        height="20"
        viewBox="0 0 13 20"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden="true"
      >
        <path
          d="M11 2L3 10L11 18"
          stroke-width="3"
        />
      </svg>
    </button>
    <button
      v-for="page in range"
      :key="page"
      class="paginator__button paginator__button--page"
      :class="{ 'paginator__button--active': page === current }"
      :aria-label="page"
      @click="go(page), gotoScroll(), pushGTM(page)"
    >
      {{ page }}
    </button>
    <button
      class="paginator__button paginator__button--control"
      :class="{ 'paginator__button--disabled': current >= total }"
      :aria-label="i18n.paginatorNext"
      @click="next(), gotoScroll()"
    >
      <svg
        width="13"
        height="20"
        viewBox="0 0 13 20"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden="true"
      >
        <path
          d="M2 2L10 10L2 18"
          stroke-width="3"
        />
      </svg>
    </button>
  </div>
</template>

<script>
export default {
  name: 'paginator',
  props: {
    total: {
      type: Number,
      required: true,
      validator(value) {
        return value >= 0
      },
    },
    current: {
      type: Number,
      required: true,
      validator(value) {
        return value > 0
      },
    },
    limit: {
      type: Number,
      required: false,
      default: 3,
    },
    i18n: {
      type: Object,
      default() {
        return {
          paginatorPrevious: 'Previous',
          paginatorNext: 'Next',
        }
      },
    },
    scrollTo: {
      type: String,
    },
  },
  emits: ['paginate'],
  setup(props, { emit }) {
    const previous = () => {
      if (props.current > 1) {
        emit('paginate', props.current - 1)
      }
    }

    const go = (page) => {
      if (page !== props.current) {
        emit('paginate', page)
      }
    }

    const next = () => {
      if (props.current < props.total) {
        emit('paginate', props.current + 1)
      }
    }
    return { previous, go, next }
  },
  computed: {
    pages() {
      const pages = []
      for (let page = 1; page <= this.total; page += 1) {
        pages.push(page)
      }
      return pages
    },
    range() {
      const halfLimit = Math.floor(this.limit / 2)
      let start = this.current - halfLimit
      let end = this.current + halfLimit

      if (start < 1) {
        start = 1
        end = Math.min(this.limit, this.total)
      }

      if (end > this.total) {
        end = this.total
        start = Math.max(this.total - this.limit + 1, 1)
      }

      const range = []
      for (let i = start; i <= end; i++) {
        range.push(i)
      }

      return range
    },
  },
  methods: {
    gotoScroll() {
      const selector = `.${this.scrollTo}`
      const el = document.querySelector(selector)
      if (el) {
        window.scrollTo({
          top: el.offsetTop,
          behavior: 'smooth',
        })
      } else {
        console.warn(`"${selector}" wasn't found`)
      }
    },
    pushGTM(page) {
      const title = document.querySelector('h1')

      window.dataLayer.push({
        event: 'tab_paginator',
        section: title.innerText || '',
        label: page
      })
    },
  },
}
</script>

<style lang="scss">
.paginator {
  $size: 2rem;

  display: flex;
  align-items: center;
  padding: 2.375rem 0;

  @include screen-size(lg) {
    padding: 2.75rem 0;
  }

  &__button {
    font-family: "Montserrat", sans-serif;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    height: $size;

    color: $blue-02;
    font-size: 1rem;
    line-height: 130%;
    text-align: center;
    user-select: none;
    cursor: pointer;

    &--control {
      svg {
        stroke: currentColor;

        &.disabled {
          stroke: $neutral-04;
        }
        &.active {
          stroke: $blue-02;
        }
      }
    }

    &--page {
      width: $size;
    }

    &--active {
      color: $blue-02;
      font-weight: 700;
    }

    &--disabled {
      opacity: 0.5;
      cursor: default;
    }
  }
}
</style>
